import type { AccountSessionDto } from '@platewell/schema';
import { useState } from 'react';
import { RelativeTime } from '../../components/relative-time.js';
import { revokeOtherSessions, revokeSession } from '../../lib/api.js';

/**
 * What is signed in to this account, and a way to end any of it.
 *
 * No location and no "active now", both of which the design asked for and neither of which
 * this app knows: nothing derives a place from the stored IP, and a session's timestamp
 * only advances once a day, so "active now" would be a guess dressed as a fact.
 */
export function DevicesList({
  sessions,
  onChanged,
}: {
  sessions: AccountSessionDto[];
  onChanged: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const others = sessions.filter((session) => !session.current);

  async function run(id: string, action: () => Promise<unknown>) {
    setBusyId(id);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not sign that device out');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <div>
        {sessions.map((session) => (
          <div className="person-row" key={session.id}>
            <span className="person-identity">
              <span className="person-name">{session.label}</span>
              <span className="person-sub">
                Last used <RelativeTime value={session.lastUsedAt} />
              </span>
            </span>
            <span className="spacer" />
            {session.current ? (
              <span className="pill pill--ok">This device</span>
            ) : (
              <button
                type="button"
                className="btn small"
                disabled={busyId != null}
                onClick={() => void run(session.id, () => revokeSession(session.id))}
              >
                {busyId === session.id ? 'Signing out…' : 'Sign out'}
              </button>
            )}
          </div>
        ))}
        {sessions.length === 0 && <p className="hint">Nothing else is signed in.</p>}
      </div>

      {others.length > 0 && (
        <div className="row">
          <button
            type="button"
            className="btn danger"
            disabled={busyId != null}
            onClick={() => void run('all', revokeOtherSessions)}
          >
            {busyId === 'all' ? 'Signing out…' : 'Sign out everywhere else'}
          </button>
        </div>
      )}

      {error != null && <p className="error-text">{error}</p>}
    </>
  );
}
